import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useSetAtom } from 'jotai';
import { FolderPlus } from 'lucide-react';

import { $createFolderInCurrentDirectory } from '../../_state/fileActions.state';
import { $showCreateFolderDialog } from '../../_state/fileDialogs.state';
import { useTranslations } from '../../i18n/useTranslations';

export const FolderCreateDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useAtom($showCreateFolderDialog);
  const createFolder = useSetAtom($createFolderInCurrentDirectory);
  const [folderName, setFolderName] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);

  const handleCreate = async () => {
    if (!folderName.trim()) {
      toast.error(t.dialogs.createFolder.errorNoName);
      return;
    }

    setIsCreating(true);

    try {
      await createFolder(folderName.trim());
      toast.success(
        t.dialogs.createFolder.successCreated.replace('{name}', folderName),
      );
      setIsOpen(false);
      setFolderName('');
    } catch {
      toast.error(t.dialogs.createFolder.errorFailed);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFolderName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isCreating && folderName.trim()) {
      handleCreate();
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className="@FolderCreateDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <FolderPlus className="size-5" />
            {t.dialogs.createFolder.title}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          <TextField
            label={t.dialogs.createFolder.label}
            placeholder={t.dialogs.createFolder.placeholder}
            value={folderName}
            onChange={setFolderName}
            onKeyDown={handleKeyDown}
            autoFocus
            isRequired
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isCreating}
          >
            {t.dialogs.createFolder.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleCreate}
            isDisabled={!folderName.trim() || isCreating}
            isLoading={isCreating}
          >
            {t.dialogs.createFolder.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
